import { useTranslation } from 'react-i18next'
import type { VariableFieldType } from '../../services/templatesApi'

export interface LocalVariableConfig {
  label: string
  field_type: VariableFieldType
  is_required: boolean
}

interface VariableConfigCardProps {
  name: string
  config: LocalVariableConfig
  onChange: (config: LocalVariableConfig) => void
}

const FIELD_TYPES: Array<{ value: VariableFieldType; labelKey: string }> = [
  { value: 'text', labelKey: 'Text' },
  { value: 'textarea', labelKey: 'Textarea' },
  { value: 'number', labelKey: 'Number' },
  { value: 'currency', labelKey: 'Currency' },
  { value: 'date', labelKey: 'Date' },
  { value: 'email', labelKey: 'Email' },
  { value: 'phone', labelKey: 'Phone' },
  { value: 'select', labelKey: 'Select' },
  { value: 'checkbox', labelKey: 'Checkbox' },
  { value: 'signature', labelKey: 'Signature' },
]

/**
 * Compact card for configuring one template variable.
 * Shows {{name}}, label input, type dropdown, and required toggle.
 */
export function VariableConfigCard({ name, config, onChange }: VariableConfigCardProps) {
  const { t } = useTranslation()

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-2.5 space-y-1.5">
      {/* Variable name */}
      <code className="text-[10px] font-mono text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-1.5 py-0.5 rounded">
        {`{{${name}}}`}
      </code>

      {/* Label input */}
      <input
        type="text"
        value={config.label}
        onChange={(e) => onChange({ ...config, label: e.target.value })}
        placeholder={name.replace(/_/g, ' ')}
        className="w-full text-xs px-2 py-1.5 rounded border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
      />

      {/* Type + Required row */}
      <div className="flex items-center gap-2">
        <select
          value={config.field_type}
          onChange={(e) => onChange({ ...config, field_type: e.target.value as VariableFieldType })}
          className="flex-1 text-[11px] px-1.5 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
        >
          {FIELD_TYPES.map((ft) => (
            <option key={ft.value} value={ft.value}>
              {ft.labelKey}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-1 text-[11px] text-neutral-500 dark:text-neutral-400 shrink-0 cursor-pointer">
          <input
            type="checkbox"
            checked={config.is_required}
            onChange={(e) => onChange({ ...config, is_required: e.target.checked })}
            className="w-3.5 h-3.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          />
          {t('wizard.required')}
        </label>
      </div>
    </div>
  )
}
